import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, Percent, Award, Lock, Tag, Gift as GiftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { useAlert } from '../../../context/AlertContext';
import { useCustomers } from '../../../context/CustomerContext';
import { usePromotionsData } from './hooks/usePromotionsData';
import { promotionsService } from '../../../services/promotionService';
import Coupons from '../Coupons';

import PromotionCard from './components/PromotionCard';
import PromotionFormModal from './components/PromotionFormModal';
import FilterDropdown from './components/FilterDropdown';

const Promotions = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { effectiveTenantId, tenantData, user, userData, hasPermission, permissionsLoading } = useAuth();
    const { showAlert } = useAlert();
    const { customers } = useCustomers();

    // Tab derived from URL so sidebar highlights correctly
    const activeTab = location.pathname.includes('coupons')
        ? 'coupons'
        : location.pathname.includes('rewards')
            ? 'rewards'
            : 'discounts';

    const [isModalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const [filters, setFilters] = useState({
        status: 'all', type: 'all', target: 'all', sortBy: 'newest'
    });

    const { promotions, menuData, loading } = usePromotionsData(effectiveTenantId, userData?.isSuperAdmin);

    const filteredPromotions = useMemo(() => {
        return (promotions || [])
            .filter(p => {
                const isCoupon = p.isCoupon === true || p.is_coupon === true;
                const isReward = p.isReward === true || p.is_reward === true;

                const tabMatch = activeTab === 'discounts' 
                    ? (!isReward && !isCoupon) 
                    : activeTab === 'rewards' 
                        ? (isReward && !isCoupon) 
                        : true;
                const searchMatch = p.title?.toLowerCase().includes(searchQuery.toLowerCase());
                const statusMatch = filters.status === 'all' || (filters.status === 'active' && p.isActive !== false) || (filters.status === 'passive' && p.isActive === false);
                const typeMatch = filters.type === 'all' || p.campaignType === filters.type || p.type === filters.type;
                const targetMatch = filters.target === 'all' || p.target === filters.target;
                return tabMatch && searchMatch && statusMatch && typeMatch && targetMatch;
            })
            .sort((a, b) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                if (filters.sortBy === 'newest') return dateB - dateA;
                if (filters.sortBy === 'oldest') return dateA - dateB;
                if (filters.sortBy === 'title') return a.title?.localeCompare(b.title);
                return 0;
            });
    }, [promotions, activeTab, searchQuery, filters]);

    const handleSave = async (formData) => {
        if (!editingItem && tenantData?.plan === 'free' && promotions.length >= 1) {
            showAlert(t('planLimits.promotionLimitReached'), 'warning');
            return;
        }
        try {
            await promotionsService.savePromotion(editingItem?.id, formData, effectiveTenantId, userData, user);
            setModalOpen(false);
        } catch (error) {
            showAlert(t('common.error'), 'error');
        }
    };

    if (permissionsLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%' }}></div></div>;

    const canView = activeTab === 'discounts' ? (userData?.isSuperAdmin || hasPermission('discounts', 'view')) : (userData?.isSuperAdmin || hasPermission('rewards', 'view'));
    if (activeTab !== 'coupons' && !canView) return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><Lock size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.5 }} /><h3>{t('common.accessDenied')}</h3><p style={{ color: 'var(--text-muted)' }}>{t('common.noPermission')}</p></div>;

    const canCreate = activeTab === 'discounts' ? hasPermission('discounts', 'create') : hasPermission('rewards', 'create');

    const tabBtnStyle = (tab) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '10px 24px',
        borderRadius: '12px',
        border: 'none',
        background: activeTab === tab ? 'var(--primary)' : 'transparent',
        color: activeTab === tab ? 'var(--text-on-primary)' : 'var(--text-main)',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
    });

    return (
        <div className="fade-in">
            <header style={{ marginBottom: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                    <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
                        {activeTab === 'discounts' ? 'İndirimler & Kampanyalar' : activeTab === 'rewards' ? t('nav.rewards') : 'Kuponlar'}
                    </h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
                        {activeTab === 'discounts' ? 'Puan çarpanları, ürün eşleşmeli indirimler ve özel fırsatlar tanımlayın.' : activeTab === 'rewards' ? t('discounts.rewardSubtitle') : 'Müşterilerinizin ödeme sırasında kullanabileceği indirim kupon kodları oluşturun.'}
                    </p>
                </div>
            </header>

            <style>{`
                .settings-header-actions .short-text, .promotions-tabs-nav .short-text { display: none; }
                .search-container { max-width: 500px; width: 100%; }

                @media (max-width: 768px) {
                    .promotions-controls-row { flex-direction: column-reverse !important; }
                    .search-container { width: 100% !important; max-width: none !important; }
                    .settings-header-actions { width: 100% !important; display: flex !important; flex-direction: row !important; gap: 8px !important; margin-top: 1rem !important; }
                    .settings-header-actions button, .settings-header-actions > div { flex: 1 !important; width: auto !important; height: auto !important; min-height: 46px !important; }
                    .settings-header-actions button { padding: 8px 4px !important; white-space: normal !important; text-align: center !important; justify-content: center !important; font-size: 0.75rem !important; }
                    .settings-header-actions .full-text, .promotions-tabs-nav .full-text { display: none !important; }
                    .settings-header-actions .short-text, .promotions-tabs-nav .short-text { display: inline !important; }
                    .settings-header-actions > div { margin-left: 0 !important; }
                    .settings-header-actions > div button { width: 100% !important; }

                    .promotions-tabs-nav { width: 100% !important; padding: 0.3rem !important; gap: 0.2rem !important; overflow: hidden !important; }
                    .promotions-tabs-nav .tab-btn {
                        flex: 1 !important;
                        flex-direction: column !important;
                        padding: 8px 1px !important;
                        font-size: 0.65rem !important;
                        gap: 4px !important;
                    }
                    .promotions-tabs-nav .tab-icon svg { width: 16px !important; height: 16px !important; }
                }
            `}</style>

            {/* Tab navigation */}
            <div style={{ marginBottom: '1.25rem' }}>
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    background: 'rgba(var(--primary-rgb), 0.05)',
                    padding: '0.5rem',
                    borderRadius: '16px',
                    width: 'fit-content',
                    maxWidth: '100%'
                }} className="promotions-tabs-nav">
                    <button
                        onClick={() => navigate('/app/campaigns')}
                        style={tabBtnStyle('discounts')}
                        className="tab-btn"
                    >
                        <span className="tab-icon" style={{ display: 'flex', alignItems: 'center' }}><Percent size={18} /></span>
                        <span className="tab-label">
                            <span className="full-text">{t('discounts.title')}</span>
                            <span className="short-text">İndirimler</span>
                        </span>
                    </button>
                    <button
                        onClick={() => navigate('/app/campaigns/rewards')}
                        style={tabBtnStyle('rewards')}
                        className="tab-btn"
                    >
                        <span className="tab-icon" style={{ display: 'flex', alignItems: 'center' }}><Award size={18} /></span>
                        <span className="tab-label">
                            <span className="full-text">{t('nav.rewards')}</span>
                            <span className="short-text">Ödüller</span>
                        </span>
                    </button>
                    <button
                        onClick={() => navigate('/app/campaigns/coupons')}
                        style={tabBtnStyle('coupons')}
                        className="tab-btn"
                    >
                        <span className="tab-icon" style={{ display: 'flex', alignItems: 'center' }}><Tag size={18} /></span>
                        <span className="tab-label">
                            <span className="full-text">Kuponlar</span>
                            <span className="short-text">Kuponlar</span>
                        </span>
                    </button>
                </div>
            </div>

            {/* Coupons tab content */}
            {activeTab === 'coupons' && <Coupons hideHeader />}

            {/* Discounts / Rewards tab content */}
            {activeTab !== 'coupons' && (
                <>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        marginBottom: '2.5rem',
                    }}>
                        <div className="promotions-controls-row" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                            <div className="search-container" style={{ position: 'relative', flex: 1, minWidth: '320px' }}>
                                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                <input placeholder={t('common.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ paddingLeft: '40px', width: '100%', height: '46px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none' }} />
                            </div>

                            <div className="settings-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {canCreate && (
                                    <button onClick={() => { setEditingItem(null); setModalOpen(true); }} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '46px', padding: '0 24px', borderRadius: '12px', border: 'none' }}>
                                        <Plus size={20} />
                                        <span className="full-text">{activeTab === 'discounts' ? t('discounts.addDiscount') : t('discounts.addReward')}</span>
                                        <span className="short-text">{activeTab === 'discounts' ? 'Kampanya Ekle' : 'Ödül Ekle'}</span>
                                    </button>
                                )}
                                <FilterDropdown filters={filters} setFilters={setFilters} activeTab={activeTab} />
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ padding: '4rem', textAlign: 'center' }}><div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto' }}></div></div>
                    ) : filteredPromotions.length === 0 ? (
                        <div className="card glass" style={{ padding: '5rem 2rem', textAlign: 'center' }}>
                            <div style={{ width: '80px', height: '80px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                                {activeTab === 'discounts' ? <Tag size={40} /> : <GiftIcon size={40} />}
                            </div>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>{activeTab === 'discounts' ? t('discounts.noDiscounts') : t('discounts.noRewards')}</h3>
                            <p style={{ color: 'var(--text-muted)' }}>{activeTab === 'discounts' ? t('discounts.noDiscountsDesc') : t('discounts.noRewardsDesc')}</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))', gap: '1.5rem' }}>
                            {filteredPromotions.map(item => (
                                <PromotionCard
                                    key={item.id} item={item} t={t}
                                    onEdit={() => { setEditingItem(item); setModalOpen(true); }}
                                    onDelete={() => promotionsService.deletePromotion(item, effectiveTenantId, userData, user)}
                                    onToggleStatus={() => promotionsService.toggleStatus(item, effectiveTenantId, userData, user)}
                                    hasEditPermission={activeTab === 'discounts' ? hasPermission('discounts', 'edit') : hasPermission('rewards', 'edit')}
                                    hasDeletePermission={activeTab === 'discounts' ? hasPermission('discounts', 'delete') : hasPermission('rewards', 'delete')}
                                />
                            ))}
                        </div>
                    )}

                    <PromotionFormModal
                        isOpen={isModalOpen}
                        onClose={() => setModalOpen(false)}
                        initialItem={editingItem}
                        onSave={handleSave}
                        menuData={menuData}
                        customers={customers}
                        tenantData={tenantData}
                        t={t}
                        isRewardMode={activeTab === 'rewards'}
                    />
                </>
            )}
        </div>
    );
};

export default Promotions;
